import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import RecipeIngredientItem from "./RecipeIngredientItem";
import AutocompleteButton from "../util/AutocompleteButton";
import Spinner from "../util/Spinner";
import { createRecipe } from "../slice/recipeSlice";
import { fetchMeasurements } from "../slice/measurementSlice";
import { fetchIngredients } from "../slice/ingredientsSlice";

const CreateRecipe = ({ closePopup }) => {
  const dispatch = useDispatch();
  const { measurements, isLoadingMeasurements } = useSelector((state) => {
    return state?.measurements;
  });
  const { ingredients, isLoadingIngredients } = useSelector((state) => {
    return state?.ingredients;
  });
  // mutable fields
  const [name, setName] = useState("");
  const [calories, setCalories] = useState(0);
  const [fromTheKitchenOf, setFromTheKitchenOf] = useState("");
  const [instructions, setInstructions] = useState("");
  const [totalTime, setTotalTime] = useState(0);
  const [selectedIngredientSid, setSelectedIngredientSid] = useState("");
  const [savingRecipe, setSavingRecipe] = useState(false);
  const [recipeIngredients, setRecipeIngredients] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    console.log("fetching measurements and ingredients");
    dispatch(fetchMeasurements());
    dispatch(fetchIngredients());
  }, [dispatch]);

  const createRecipeHandler = async (e) => {
    e.preventDefault();
    try {
      setSavingRecipe(true);
      const postData = {
        name,
        calories,
        fromTheKitchenOf,
        instructions,
        totalTime,
        recipeIngredients,
      };
      console.log("postData is ", postData);
      const resultAction = await dispatch(createRecipe(postData));
      if (createRecipe.fulfilled.match(resultAction)) {
        console.log("successfully created recipe!");
        navigate("/recipes?refresh=true");
        setSavingRecipe(false);
        closePopup();
      } else {
        console.log("create recipe failed");
      }
    } catch (error) {
      console.log("error creating a new recipe, message: ", error.message);
    } finally {
      setSavingRecipe(false);
    }
  };

  const removeRecipeIngredient = (recipeIngredient) => {
    console.log("removing recipe ingredient from list: ", recipeIngredient);
    setRecipeIngredients((prev) =>
      prev.filter((ri) => ri?.ingredientSid !== recipeIngredient?.ingredientSid)
    );
  };

  const updateMeasurement = (recipeIngredient, measurementSid) => {
    console.log(
      "CreateRecipe updating " +
        recipeIngredient?.ingredientSid +
        " measurement to " +
        measurementSid
    );
    setRecipeIngredients((prevItems) =>
      prevItems.map((ri) =>
        recipeIngredient?.ingredientSid === ri?.ingredientSid
          ? {
              ...ri,
              measurementSid,
            }
          : ri
      )
    );
  };

  const updateQuantity = (recipeIngredient, quantity) => {
    console.log(
      "CreateRecipe updating " +
        recipeIngredient?.ingredientSid +
        " quantity to " +
        quantity
    );
    setRecipeIngredients((prevItems) =>
      prevItems.map((ri) =>
        recipeIngredient?.ingredientSid === ri?.ingredientSid
          ? {
              ...ri,
              quantity,
            }
          : ri
      )
    );
  };

  const addRecipeIngredient = () => {
    console.log("addRecipeIngredient invoked with sid ", selectedIngredientSid);
    const hasRecipeIngredient = recipeIngredients.some(
      (ri) => ri.ingredientSid === selectedIngredientSid
    );
    if (!hasRecipeIngredient) {
      const ingredient = ingredients.find(
        (item) => item?.sid === selectedIngredientSid
      );
      console.log("Adding recipe ingredient to list ", ingredient);
      setRecipeIngredients((prevList) => [
        {
          ingredientSid: ingredient?.sid,
          name: ingredient?.name,
          isMeasured: ingredient?.ingredientType.isMeasured,
          measurementSid: ingredient?.measurementSid,
          quantity: 1,
        },
        ...prevList,
      ]);
    } else {
      console.log(
        "Not adding recipe ingredient " +
          selectedIngredientSid +
          " to list--it's already there!"
      );
    }
    setSelectedIngredientSid("");
  };

  const inputClasses =
    "w-full border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      {isLoadingMeasurements && isLoadingIngredients ? (
        <Spinner />
      ) : (
        <div className="bg-white rounded-lg p-6 w-full sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <h2 className="text-lg font-semibold text-stone-800 mb-4">
            New recipe
          </h2>
          <form onSubmit={createRecipeHandler} className="space-y-3">
            <input
              type="text"
              placeholder="Name"
              className={inputClasses}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            ></input>
            <input
              type="text"
              placeholder="Calories"
              className={inputClasses}
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
            ></input>
            <input
              type="text"
              placeholder="From the Kitchen Of"
              className={inputClasses}
              value={fromTheKitchenOf}
              onChange={(e) => setFromTheKitchenOf(e.target.value)}
            ></input>
            <input
              type="text"
              placeholder="Total time"
              className={inputClasses}
              value={totalTime}
              onChange={(e) => setTotalTime(e.target.value)}
            ></input>
            <textarea
              type="text"
              placeholder="Instructions"
              className={`${inputClasses} min-h-[100px]`}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              required
            ></textarea>
            <hr className="border-stone-200" />
            <fieldset>
              <legend className="text-sm font-medium text-stone-500 mb-2">
                Add ingredients
              </legend>
              <AutocompleteButton
                items={ingredients}
                updateItems={(i) => setSelectedIngredientSid(i?.sid)}
                buttonHandler={addRecipeIngredient}
              />
            </fieldset>
            <fieldset>
              <ul className="space-y-2">
                {recipeIngredients.map((recipeIngredient) => {
                  return (
                    <RecipeIngredientItem
                      key={recipeIngredient?.ingredientSid}
                      ingredient={recipeIngredient}
                      removeRecipeIngredient={removeRecipeIngredient}
                      measurements={measurements}
                      updateMeasurement={updateMeasurement}
                      updateQuantity={updateQuantity}
                    />
                  );
                })}
              </ul>
            </fieldset>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={closePopup} className="btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingRecipe}
                className="btn-primary"
              >
                {savingRecipe ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CreateRecipe;
